import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getGraphqlUrl } from './environments';

const getBearerAuthToken = (localStorage: any): string => {
  if (!JSON.parse(localStorage['okta-token-storage']).idToken)
    return 'Bearer 0';
  try {
    return `Bearer ${
      JSON.parse(localStorage['okta-token-storage']).idToken.idToken
    }`;
  } catch (err) {
    return '';
  }
};

const authMiddleware = new ApolloLink((operation, forward) => {
  // @ts-ignore
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'Content-Type': 'application/json',
      Authorization: getBearerAuthToken(localStorage),
    },
  }));
  return forward(operation);
});

const httpLink = createHttpLink({
  uri: getGraphqlUrl(),
  credentials: 'same-origin',
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );

  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const retryLink = new RetryLink({
  delay: {
    initial: 500,
    max: 3000,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => !!error,
  },
});

const graphqlClient = new ApolloClient({
  cache: new InMemoryCache({ addTypename: false }),
  link: from([authMiddleware, retryLink, errorLink, httpLink]),
});

export const getGraphqlClient = () => graphqlClient;

export const refetchActiveQueries = () =>
  graphqlClient.refetchQueries({ include: 'active' });

/**
 * Invalidate queries in the cache based on the query name and the string that the query contains.
 * @param queries - An array of objects containing the query name and the string to invalidate the query if it contains.
 * @param queries[].query - Which query to invalidate.
 * @param queries[].invalidateIfContains - Invalidate the query if it contains this string. Otherwise, invalidate the entire query.
 */
export const invalidateQueries = (
  queries: { query: string; invalidateIfContains: string }[],
) => {
  queries.forEach(({ query, invalidateIfContains }) => {
    graphqlClient.cache.modify({
      fields: {
        [query]: (existing, { storeFieldName }) => {
          if (invalidateIfContains) {
            if (storeFieldName.includes(invalidateIfContains)) {
              return undefined;
            }
            return existing;
          }
          return undefined;
        },
      },
    });
    graphqlClient.cache.gc();
  });
};
